import React, { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Typography, Paper, CircularProgress, Button } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import { styled } from '@mui/material/styles'

import Spacer from '../../../../common/components/Spacer'
import Copyright from '../../../../common/components/Copyright'
import useVerifyDomainToken from './hooks/useVerifyDomainToken'
import { Provider } from 'react-redux'
import { store } from '../../../../api/store'

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default
}))

const ContentPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(4),
  maxWidth: '500px',
  width: '100%',
  borderRadius: '16px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
}))

const SuccessIcon = styled(CheckCircleOutlineIcon)(({ theme }) => ({
  fontSize: '80px',
  color: theme.palette.success.main,
  marginBottom: theme.spacing(2)
}))

const ErrorIcon = styled(ErrorOutlineIcon)(({ theme }) => ({
  fontSize: '80px',
  color: theme.palette.error.main,
  marginBottom: theme.spacing(2)
}))

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
  textAlign: 'center'
}))

const Message = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  marginBottom: theme.spacing(3)
}))

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: '12px',
  padding: theme.spacing(1, 3)
}))

const VerifyDomainEmailWrapped = () => {
  return (
    <Provider store={store}>
      <VerifyDomainEmailPage />
    </Provider>
  )
}

const VerifyDomainEmailPage = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const navigate = useNavigate()

  const { status, errorMessage, verifyToken } = useVerifyDomainToken(token)

  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        navigate('/learn')
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [status, navigate])

  const handleRetry = () => {
    verifyToken()
  }

  const handleGoToOnboarding = () => {
    navigate('/learn')
  }
  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <CircularProgress size={80} sx={{ marginBottom: 2 }} />
            <Title variant='h5'>Verifying Your Domain</Title>
            <Message variant='body1'>Please wait while we verify your domain...</Message>
          </>
        )
      case 'success':
        return (
          <>
            <SuccessIcon />
            <Title variant='h5'>Domain Verified Successfully!</Title>
            <Message variant='body1'>
              Your domain has been verified. You will be redirected to onboarding page in a few seconds.
            </Message>
            <ActionButton variant='contained' color='primary' onClick={handleGoToOnboarding}>
              Back to Onboarding
            </ActionButton>
          </>
        )
      case 'error':
        return (
          <>
            <ErrorIcon />
            <Title variant='h5'>Verification Failed</Title>
            <Message variant='body1'>{errorMessage}</Message>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <ActionButton variant='outlined' color='primary' onClick={handleRetry} startIcon={<RefreshIcon />}>
                Try Again
              </ActionButton>
            </Box>
          </>
        )
      default:
        return null
    }
  }

  return (
    <PageContainer>
      <Spacer />
      <ContentPaper>{renderContent()}</ContentPaper>
      <Spacer />
      <Copyright />
    </PageContainer>
  )
}

export default VerifyDomainEmailWrapped
